import AttachmentIcon from "@mui/icons-material/Attachment";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { getCoreUserFullName } from "../../../common/utils/users";
import { fetchCustomerOrgNames } from "../../customer/customerOrganizationsSlice";
import { fetchExpertUsersByIds } from "../../user/expertUsersSlice";
import UserMessageChannelIcons from "../../userMessages/UserMessageChannelIcons";
import {
  getCustomerOrgIdFromContext,
  getCustomerOrgIdsFromMessages,
  getInAppReadReceipt,
  getMessageListSecondary,
  getMissingUserIds,
  getSenderDisplayName,
  getSenderUserIds,
  isInAppNotice,
  normalizeUserMessageChannels,
} from "../../userMessages/userMessagesHelpers";
import {
  fetchUserMessageById,
  fetchUserMessages,
} from "../../userMessages/userMessagesSlice";
import {
  UserMessageByIdResponse,
  UserMessageRow,
} from "../../userMessages/userMessagesTypes";
import UserMessageDialog from "./UserMessageDialog";

interface Props {
  talentId: string;
}

const UserMessagesList = ({ talentId }: Props) => {
  const { t } = useTranslation("userMessages");
  const dispatch = useAppDispatch();
  const expertEntities = useAppSelector((state) => state.expertUsers.entities);
  const requestedSenderIdsRef = useRef<Set<string>>(new Set());
  const [settledSenderIds, setSettledSenderIds] = useState<Set<string>>(
    () => new Set(),
  );

  const [messages, setMessages] = useState<UserMessageRow[]>([]);
  const [selectedMessage, setSelectedMessage] =
    useState<UserMessageByIdResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [customerOrgNames, setCustomerOrgNames] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setIsLoadingList(true);
    dispatch(fetchUserMessages({ recipientUserId: talentId })).then(
      (action) => {
        setIsLoadingList(false);
        if (isFulfilled(action)) {
          setMessages(action.payload.data);
        }
      },
    );
  }, [talentId, dispatch]);

  useEffect(() => {
    const senderIds = getSenderUserIds(
      messages.filter((msg) => !isInAppNotice(msg)),
    );
    const idsToFetch = getMissingUserIds(
      senderIds,
      expertEntities,
      requestedSenderIdsRef.current,
    );
    if (idsToFetch.length > 0) {
      idsToFetch.forEach((id) => requestedSenderIdsRef.current.add(id));
      dispatch(fetchExpertUsersByIds({ userIds: idsToFetch })).finally(() => {
        setSettledSenderIds((prev) => {
          const next = new Set(prev);
          idsToFetch.forEach((id) => next.add(id));
          return next;
        });
      });
    }

    const missingCustomerOrgIds = getCustomerOrgIdsFromMessages(
      messages,
    ).filter((id) => !customerOrgNames[id]);
    if (missingCustomerOrgIds.length === 0) return;

    dispatch(
      fetchCustomerOrgNames({ ids: missingCustomerOrgIds.join(",") }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setCustomerOrgNames((prev) => ({
          ...prev,
          ...Object.fromEntries(
            action.payload.map((org) => [org.id, org.name]),
          ),
        }));
      }
    });
  }, [messages, dispatch, expertEntities, customerOrgNames]);

  const handleOpenMessage = async (messageId: string) => {
    setDialogOpen(true);
    setSelectedMessage(null);
    setIsLoadingDetail(true);
    const action = await dispatch(
      fetchUserMessageById({ messageId, recipientUserId: talentId }),
    );
    setIsLoadingDetail(false);
    if (isFulfilled(action)) {
      setSelectedMessage(action.payload);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMessage(null);
  };

  const getSenderName = (createdByUserId: string) => {
    const expert = expertEntities[createdByUserId];
    return expert ? getCoreUserFullName(expert) : undefined;
  };

  const isSenderLookupSettled = (createdByUserId: string) =>
    Boolean(expertEntities[createdByUserId]) ||
    settledSenderIds.has(createdByUserId);

  const senderUnavailable = t("senderUnavailable");
  const inAppSenderName = t("inAppSender");
  const selectedIsInAppNotice = selectedMessage
    ? isInAppNotice(selectedMessage)
    : false;
  const selectedSenderName = selectedMessage
    ? getSenderName(selectedMessage.createdByUserId)
    : undefined;
  const selectedSenderLookupSettled = selectedMessage
    ? isSenderLookupSettled(selectedMessage.createdByUserId)
    : false;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100%",
        minHeight: 0,
        paddingLeft: "7px",
      }}
    >
      <Typography
        variant="header"
        sx={{
          textTransform: "uppercase",
          fontWeight: 700,
          px: "8px",
          mb: 0.5,
          flexShrink: 0,
        }}
      >
        {t("title")}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {isLoadingList ? (
          <LoadingSpinner sx={{ justifyContent: "center", px: "8px" }} />
        ) : messages.length === 0 ? (
          <Typography variant="secondary" color="text.secondary" px="8px">
            {t("noMessages")}
          </Typography>
        ) : (
          <List disablePadding sx={{ width: "inherit" }}>
            {messages
              .filter((msg) => msg.status !== "Draft")
              .map((msg) => {
                const readReceipt = getInAppReadReceipt(msg);
                const notice = isInAppNotice(msg);
                const secondary = getMessageListSecondary(
                  msg,
                  notice
                    ? inAppSenderName
                    : getSenderDisplayName(
                        getSenderName(msg.createdByUserId),
                        isSenderLookupSettled(msg.createdByUserId),
                        senderUnavailable,
                      ),
                  {
                    processing: t("processing"),
                    sendFailed: t("sendFailed"),
                  },
                );

                return (
                  <ListItem key={msg.messageId} disablePadding>
                    <ListItemButton
                      onClick={() => handleOpenMessage(msg.messageId)}
                      sx={{ padding: "4px 8px", borderRadius: 1 }}
                    >
                      <ListItemAvatar sx={{ minWidth: "auto", mr: 1.5 }}>
                        <UserMessageChannelIcons
                            channels={normalizeUserMessageChannels(msg.channels)}
                          framed
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="inherit"
                            fontWeight={600}
                            noWrap
                            sx={{ fontSize: "13px" }}
                          >
                            {msg.subject}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="secondary"
                            component="span"
                            color={secondary.isError ? "error" : undefined}
                          >
                            {secondary.text}
                            {readReceipt?.kind === "read" && (
                              <Tooltip
                                title={`${t("readAt")} ${readReceipt.date}`}
                              >
                                <CheckIcon
                                  aria-label={`${t("readAt")} ${readReceipt.date}`}
                                  sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    verticalAlign: "middle",
                                    ml: secondary.text ? "6px" : 0,
                                    mb: secondary.text ? "4px" : 0,
                                  }}
                                />
                              </Tooltip>
                            )}
                            {msg.hasAttachments && (
                              <AttachmentIcon
                                sx={{
                                  fontSize: 16,
                                  opacity: 0.6,
                                  verticalAlign: "middle",
                                  ml: "4px",
                                }}
                              />
                            )}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
          </List>
        )}
      </Box>
      <UserMessageDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        message={selectedMessage}
        isLoading={
          isLoadingDetail ||
          (selectedMessage != null &&
            !selectedIsInAppNotice &&
            !selectedSenderLookupSettled)
        }
        senderName={
          selectedIsInAppNotice
            ? inAppSenderName
            : getSenderDisplayName(
                selectedSenderName,
                selectedSenderLookupSettled,
                senderUnavailable,
              ) ?? ""
        }
        hasSenderDetails={selectedIsInAppNotice || Boolean(selectedSenderName)}
        isInAppNotice={selectedIsInAppNotice}
        customerName={
          selectedMessage
            ? customerOrgNames[
                getCustomerOrgIdFromContext(selectedMessage.context) ?? ""
              ] ?? ""
            : ""
        }
      />
    </Box>
  );
};

export default UserMessagesList;
